import { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Mail, Phone, Plus, Search, User } from 'lucide-react';
import { AppLayout } from '@/components/ui/AppLayout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Banner } from '@/components/ui/Banner';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input, Label } from '@/components/ui/Input';
import { Badge } from '@/components/ui/StatusBadge';
import { useToast } from '@/components/ui/Toast';
import { useAuthStore } from '@/stores/auth';
import {
  apporteursApi,
  apporteurDisplayName,
  type ApporteurCreatePayload,
  type ApporteurItem,
  type ApporteurType,
  type ApporteurUpdatePayload,
} from '@/services/apporteurs.api';

/**
 * Page Apporteurs - CRUD pour les apporteurs d'affaires.
 *
 * Refonte 2026-05-22 : entité polymorphe (person | company). Le toggle de
 * type apparaît dans la modale de création (calqué sur CreateProspectModal),
 * pas dans la liste — où on affiche un pictogramme (User / Building2) pour
 * discriminer visuellement.
 *
 * Visible pour tous les writers (admin/director/manager/collaborator).
 * Pas de scope cloisonnement : entité globale.
 */
export function ApporteursPage() {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const [items, setItems] = useState<ApporteurItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | ApporteurType>('all');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<ApporteurItem | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const filters: { is_active?: boolean; type?: ApporteurType } = {};
      if (!showInactive) filters.is_active = true;
      if (typeFilter !== 'all') filters.type = typeFilter;
      const list = await apporteursApi.list(token, filters);
      setItems(list);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    }
  }, [token, showInactive, typeFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  // Filtre par search côté client (le backend supporte search aussi mais ici
  // on évite un re-fetch à chaque keystroke)
  const filtered = useMemo(() => {
    if (!items || !search.trim()) return items ?? [];
    const term = search.trim().toLowerCase();
    return items.filter((a) => {
      const display = apporteurDisplayName(a).toLowerCase();
      return (
        display.includes(term) ||
        (a.firstName ?? '').toLowerCase().includes(term) ||
        (a.lastName ?? '').toLowerCase().includes(term) ||
        (a.companyName ?? '').toLowerCase().includes(term) ||
        (a.company ?? '').toLowerCase().includes(term) ||
        (a.email ?? '').toLowerCase().includes(term) ||
        (a.phone ?? '').toLowerCase().includes(term) ||
        (a.externalId ?? '').toLowerCase().includes(term)
      );
    });
  }, [items, search]);

  async function toggleActive(a: ApporteurItem) {
    try {
      await apporteursApi.setActive(token, a.id, !a.isActive);
      toast({
        tone: 'success',
        title: a.isActive ? 'Apporteur désactivé' : 'Apporteur activé',
        message: apporteurDisplayName(a),
      });
      await load();
    } catch (err) {
      toast({
        tone: 'danger',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    }
  }

  return (
    <AppLayout>
      <PageHeader
        title="Apporteurs"
        subtitle="Apporteurs d'affaires — personnes physiques ou structures qui amènent des opportunités au cabinet en échange d'une rétrocommission."
        actions={
          <Button leadingIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setCreating(true)}>
            Nouvel apporteur
          </Button>
        }
      />

      {error && <Banner tone="danger">{error}</Banner>}

      <Card>
        {/* Toolbar */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[260px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <Input
              placeholder="Rechercher par nom, raison sociale, entreprise, email, ID externe…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          {/* Filtre type : segmented control discret (Tous / Personnes / Entreprises). */}
          <div className="flex shrink-0 rounded-md border border-border-subtle bg-gray-50 p-0.5 text-xs font-medium">
            {[
              { v: 'all' as const, label: 'Tous' },
              { v: 'person' as const, label: 'Personnes', icon: User },
              { v: 'company' as const, label: 'Entreprises', icon: Building2 },
            ].map(({ v, label, icon: Icon }) => (
              <button
                key={v}
                type="button"
                onClick={() => setTypeFilter(v)}
                className={`flex items-center gap-1.5 rounded px-2.5 py-1 transition ${
                  typeFilter === v
                    ? 'bg-white text-brand shadow-sm'
                    : 'text-text-muted hover:text-brand'
                }`}
                aria-pressed={typeFilter === v}
              >
                {Icon && <Icon className="h-3.5 w-3.5" />}
                {label}
              </button>
            ))}
          </div>
          <label className="flex shrink-0 items-center gap-2 text-sm text-text-muted">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-accent focus:ring-accent"
            />
            Afficher les désactivés
          </label>
        </div>

        {/* Liste */}
        {items === null ? (
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title="Aucun apporteur"
            description={
              search
                ? `Aucun résultat pour « ${search} ».`
                : 'Créez le premier apporteur pour démarrer.'
            }
          />
        ) : (
          <ul className="divide-y divide-gray-100">
            {filtered.map((a) => (
              <li key={a.id} className="py-3">
                <div className="flex items-start justify-between gap-4">
                  <button
                    type="button"
                    onClick={() => setEditing(a)}
                    className="min-w-0 flex-1 text-left transition-colors hover:opacity-80"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Pictogramme discriminant person / company */}
                      {a.type === 'company' ? (
                        <Building2 className="h-4 w-4 shrink-0 text-text-muted" />
                      ) : (
                        <User className="h-4 w-4 shrink-0 text-text-muted" />
                      )}
                      <span className="font-semibold text-brand">{apporteurDisplayName(a)}</span>
                      {/* 3 mini-badges des taux (com / frais / honoraires) —
                          décision 2026-04-30. Chaque badge affiche son intitulé
                          court devant le pourcentage. */}
                      <span title="Taux sur la commission">
                        <Badge tone="purple">
                          Com{' '}
                          {Number(a.retrocommissionRate)
                            .toFixed(2)
                            .replace(/\.?0+$/, '')}{' '}
                          %
                        </Badge>
                      </span>
                      <span title="Taux sur les frais">
                        <Badge tone="purple">
                          Frais{' '}
                          {Number(a.retrocommissionRateFees)
                            .toFixed(2)
                            .replace(/\.?0+$/, '')}{' '}
                          %
                        </Badge>
                      </span>
                      <span title="Taux sur les honoraires">
                        <Badge tone="purple">
                          Hono{' '}
                          {Number(a.retrocommissionRateHonoraires)
                            .toFixed(2)
                            .replace(/\.?0+$/, '')}{' '}
                          %
                        </Badge>
                      </span>
                      {a.externalId && (
                        <span
                          title="Identifiant dans l'ancien logiciel"
                          className="font-mono text-[10px] text-text-muted"
                        >
                          #{a.externalId}
                        </span>
                      )}
                      {!a.isActive && <Badge tone="neutral">Désactivé</Badge>}
                    </div>
                    {/* Sous-ligne entreprise : pour person on affiche
                        l'employeur (a.company), pour company on n'affiche
                        rien ici (la raison sociale est déjà en titre). */}
                    {a.type === 'person' && a.company && (
                      <div className="mt-0.5 flex items-center gap-1.5 text-xs text-text-muted">
                        <Building2 className="h-3 w-3" />
                        {a.company}
                      </div>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-text-muted">
                      {a.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {a.email}
                        </span>
                      )}
                      {a.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {a.phone}
                        </span>
                      )}
                      {(a.city || a.postalCode) && (
                        <span>{[a.postalCode, a.city].filter(Boolean).join(' ')}</span>
                      )}
                    </div>
                  </button>
                  <div className="shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        void toggleActive(a);
                      }}
                    >
                      {a.isActive ? 'Désactiver' : 'Activer'}
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {creating && (
        <ApporteurFormModal
          mode="create"
          initial={null}
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await load();
          }}
        />
      )}
      {editing && (
        <ApporteurFormModal
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
    </AppLayout>
  );
}

// ---------------------------------------------------------------------------
// Modale create / edit
// ---------------------------------------------------------------------------

function ApporteurFormModal({
  mode,
  initial,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit';
  initial: ApporteurItem | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);

  // Type discriminant. À la création on permet le toggle (calqué
  // CreateProspectModal). À l'édition, type immuable côté API → on lit
  // celui de l'apporteur existant et on ne montre PAS le toggle.
  const [type, setType] = useState<ApporteurType>(initial?.type ?? 'person');

  const [firstName, setFirstName] = useState(initial?.firstName ?? '');
  const [lastName, setLastName] = useState(initial?.lastName ?? '');
  const [companyName, setCompanyName] = useState(initial?.companyName ?? '');
  const [externalId, setExternalId] = useState(initial?.externalId ?? '');

  // 3 taux saisis en string pour permettre l'état intermédiaire vide / "12,5".
  // Converti en number à la soumission. Defaults à la création (décision
  // produit 2026-06-27 — commission passée de 20 à 30) :
  //   - retroRate (commission) = '30'
  //   - retroRateFees / retroRateHonoraires = '0' (jamais facturés par défaut)
  const [retroRate, setRetroRate] = useState<string>(initial?.retrocommissionRate ?? '30');
  const [retroRateFees, setRetroRateFees] = useState<string>(
    initial?.retrocommissionRateFees ?? '0',
  );
  const [retroRateHonoraires, setRetroRateHonoraires] = useState<string>(
    initial?.retrocommissionRateHonoraires ?? '0',
  );
  const [company, setCompany] = useState(initial?.company ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [street, setStreet] = useState(initial?.street ?? '');
  const [postalCode, setPostalCode] = useState(initial?.postalCode ?? '');
  const [city, setCity] = useState(initial?.city ?? '');
  // Flag verrou + count (fetché en mode edit pour afficher le tooltip).
  const [lockedByWonOpps, setLockedByWonOpps] = useState(false);
  const [wonOppsCount, setWonOppsCount] = useState(0);
  // Détecte si user.role === 'admin' pour signaler le bypass possible
  // côté UI. Pas un secret côté serveur (la garde finale est backend).
  const userRole = useAuthStore((s) => s.user?.role);
  const isAdmin = userRole === 'admin';

  // Fetch détail en mode edit : la liste ne contient pas lockedByWonOpps.
  useEffect(() => {
    if (mode !== 'edit' || !initial) return;
    let cancelled = false;
    void apporteursApi
      .get(token, initial.id)
      .then((detail) => {
        if (cancelled) return;
        setLockedByWonOpps(detail.lockedByWonOpps ?? false);
        setWonOppsCount(detail.wonOppsCount ?? 0);
      })
      .catch(() => {
        // Non-bloquant : on laisse l'utilisateur tenter, le 422 backend
        // remontera si besoin.
      });
    return () => {
      cancelled = true;
    };
  }, [mode, initial, token]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  // Validation form. Taux : [0,100], accepte virgule FR.
  const parsedRate = Number(retroRate.replace(',', '.'));
  const parsedRateFees = Number(retroRateFees.replace(',', '.'));
  const parsedRateHono = Number(retroRateHonoraires.replace(',', '.'));
  const isOneRateValid = (s: string, n: number) =>
    s.trim() !== '' && Number.isFinite(n) && n >= 0 && n <= 100;
  const isRateValid =
    isOneRateValid(retroRate, parsedRate) &&
    isOneRateValid(retroRateFees, parsedRateFees) &&
    isOneRateValid(retroRateHonoraires, parsedRateHono);

  const identityValid =
    type === 'person'
      ? firstName.trim().length > 0 && lastName.trim().length > 0
      : companyName.trim().length > 0;

  const isValid = identityValid && isRateValid;

  // Le champ taux est verrouillé en édition si :
  //  - l'apporteur a ≥1 opp signée ET
  //  - l'utilisateur courant n'est pas admin
  const rateFieldDisabled = mode === 'edit' && lockedByWonOpps && !isAdmin;

  function handleTypeChange(next: ApporteurType) {
    // Reset cross-type pour éviter des champs fantômes à la soumission.
    setType(next);
    if (next === 'person') {
      setCompanyName('');
    } else {
      setFirstName('');
      setLastName('');
      setCompany(''); // l'employeur n'a pas de sens pour une entreprise
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      if (mode === 'create') {
        const common = {
          retrocommissionRate: parsedRate,
          retrocommissionRateFees: parsedRateFees,
          retrocommissionRateHonoraires: parsedRateHono,
          ...(externalId.trim() ? { externalId: externalId.trim() } : {}),
          ...(company.trim() ? { company: company.trim() } : {}),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
          ...(email.trim() ? { email: email.trim() } : {}),
          ...(street.trim() ? { street: street.trim() } : {}),
          ...(postalCode.trim() ? { postalCode: postalCode.trim() } : {}),
          ...(city.trim() ? { city: city.trim() } : {}),
        };
        const payload: ApporteurCreatePayload =
          type === 'person'
            ? {
                type: 'person',
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                ...common,
              }
            : {
                type: 'company',
                companyName: companyName.trim(),
                ...common,
              };
        const created = await apporteursApi.create(token, payload);
        toast({
          tone: 'success',
          title: 'Apporteur créé',
          message: apporteurDisplayName(created),
        });
      } else if (initial) {
        // En édition : on n'envoie QUE les champs pertinents au type existant
        // (le backend les ignore silencieusement mais autant garder le payload propre).
        const patch: ApporteurUpdatePayload = {
          retrocommissionRate: parsedRate,
          retrocommissionRateFees: parsedRateFees,
          retrocommissionRateHonoraires: parsedRateHono,
          externalId: externalId.trim() || null,
          company: type === 'person' ? company.trim() || null : null,
          phone: phone.trim() || null,
          email: email.trim() || null,
          street: street.trim() || null,
          postalCode: postalCode.trim() || null,
          city: city.trim() || null,
        };
        if (type === 'person') {
          patch.firstName = firstName.trim();
          patch.lastName = lastName.trim();
        } else {
          patch.companyName = companyName.trim();
        }
        const updated = await apporteursApi.update(token, initial.id, patch);
        toast({
          tone: 'success',
          title: 'Apporteur mis à jour',
          message: apporteurDisplayName(updated),
        });
      }
      await onSaved();
    } catch (err) {
      toast({
        tone: 'danger',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="animate-fade-in fixed inset-0 z-50 flex items-center justify-center bg-brand/30 px-4 backdrop-blur-sm">
      <form
        onSubmit={handleSubmit}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-modal"
      >
        <h2 className="text-lg font-semibold text-brand">
          {mode === 'create' ? 'Nouvel apporteur' : "Modifier l'apporteur"}
        </h2>
        <p className="mt-1 text-sm text-text-muted">
          {mode === 'create'
            ? 'Personne physique ou structure morale (cabinet partenaire, banque, etc.).'
            : 'Le type de l’apporteur (personne / entreprise) ne se change pas après création.'}
        </p>

        {/* Toggle type Entreprise / Particulier — pattern calqué sur
            CreateProspectModal pour cohérence UX. Visible uniquement en
            mode 'create' (type immuable au PATCH). */}
        {mode === 'create' && (
          <div className="mt-5 flex gap-2 rounded-md bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => handleTypeChange('person')}
              className={`flex flex-1 items-center justify-center gap-2 rounded px-3 py-1.5 text-sm font-medium transition ${
                type === 'person'
                  ? 'bg-white text-brand shadow-sm'
                  : 'text-text-muted hover:text-brand'
              }`}
              aria-pressed={type === 'person'}
            >
              <User className="h-4 w-4" />
              Personne
            </button>
            <button
              type="button"
              onClick={() => handleTypeChange('company')}
              className={`flex flex-1 items-center justify-center gap-2 rounded px-3 py-1.5 text-sm font-medium transition ${
                type === 'company'
                  ? 'bg-white text-brand shadow-sm'
                  : 'text-text-muted hover:text-brand'
              }`}
              aria-pressed={type === 'company'}
            >
              <Building2 className="h-4 w-4" />
              Entreprise
            </button>
          </div>
        )}

        <div className="mt-5 grid grid-cols-2 gap-4">
          {/* Identité : champs conditionnels selon `type` */}
          {type === 'person' ? (
            <>
              <div>
                <Label htmlFor="firstName">Prénom *</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="lastName">Nom *</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </>
          ) : (
            <div className="col-span-2">
              <Label htmlFor="companyName">Raison sociale *</Label>
              <Input
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Cabinet Dupont, Banque XYZ, …"
                required
                autoFocus
              />
            </div>
          )}

          {/* External ID : optionnel, lecture seule en mode edit pour ne pas
              casser un mapping legacy par mégarde. En création, modifiable. */}
          <div className="col-span-2">
            <Label htmlFor="externalId">
              Identifiant externe
              <span className="ml-1.5 text-[11px] font-normal text-text-muted">
                (ID dans l’ancien logiciel — optionnel)
              </span>
            </Label>
            <Input
              id="externalId"
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
              disabled={mode === 'edit'}
              placeholder="Ex. legacy_apport_4287"
              className={mode === 'edit' ? 'font-mono text-sm' : 'font-mono'}
              maxLength={64}
              title={
                mode === 'edit'
                  ? "L'identifiant externe ne se modifie pas depuis l'UI (intégrité du mapping legacy). Utilisez l'API directement si nécessaire."
                  : undefined
              }
            />
          </div>

          {/* 3 taux de rétrocommission côte à côte (décision 2026-04-30 Q8).
              Tous obligatoires, [0..100], verrouillés ensemble si ≥1 opp won. */}
          <div className="col-span-2">
            <Label>Taux de rétrocommission (%) *</Label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Input
                  id="retroRate"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={retroRate}
                  onChange={(e) => setRetroRate(e.target.value)}
                  disabled={rateFieldDisabled}
                  required
                  aria-label="Taux sur la commission"
                  title={
                    rateFieldDisabled
                      ? `Verrouillé : ${wonOppsCount} opportunité(s) signée(s) référencent cet apporteur. Un administrateur peut forcer la modification.`
                      : 'Sur la commission'
                  }
                />
                <p className="mt-1 text-[11px] text-text-muted">Sur commission</p>
              </div>
              <div>
                <Input
                  id="retroRateFees"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={retroRateFees}
                  onChange={(e) => setRetroRateFees(e.target.value)}
                  disabled={rateFieldDisabled}
                  required
                  aria-label="Taux sur les frais"
                  title={rateFieldDisabled ? undefined : 'Sur les frais'}
                />
                <p className="mt-1 text-[11px] text-text-muted">Sur frais</p>
              </div>
              <div>
                <Input
                  id="retroRateHonoraires"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  value={retroRateHonoraires}
                  onChange={(e) => setRetroRateHonoraires(e.target.value)}
                  disabled={rateFieldDisabled}
                  required
                  aria-label="Taux sur les honoraires"
                  title={rateFieldDisabled ? undefined : 'Sur les honoraires'}
                />
                <p className="mt-1 text-[11px] text-text-muted">Sur honoraires</p>
              </div>
            </div>
            <p id="retroRate-hint" className="mt-2 text-xs text-text-muted">
              {rateFieldDisabled ? (
                <span className="text-warning-700">
                  Verrouillé : {wonOppsCount} opportunité(s) signée(s) référencent cet apporteur. Un
                  administrateur peut forcer la modification des 3 taux.
                </span>
              ) : (
                <>
                  Appliqués à toutes les opportunités liées.
                  {mode === 'edit' && lockedByWonOpps && isAdmin && (
                    <span className="ml-1 text-warning-700">
                      ⚠ {wonOppsCount} opp(s) signée(s) - bypass admin actif.
                    </span>
                  )}
                </>
              )}
            </p>
          </div>

          {/* Champ « Entreprise » : sert d'employeur pour une person (un
              indépendant rattaché à un cabinet par exemple). Pas affiché en
              type=company (redondant avec la raison sociale). */}
          {type === 'person' && (
            <div className="col-span-2">
              <Label htmlFor="company">Entreprise (employeur)</Label>
              <Input
                id="company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Cabinet X, indépendant, …"
              />
            </div>
          )}

          <div>
            <Label htmlFor="phone">Téléphone</Label>
            <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="col-span-2">
            <Label htmlFor="street">Adresse</Label>
            <Input id="street" value={street} onChange={(e) => setStreet(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="postalCode">Code postal</Label>
            <Input
              id="postalCode"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="city">Ville</Label>
            <Input id="city" value={city} onChange={(e) => setCity(e.target.value)} />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={onClose} disabled={submitting}>
            Annuler
          </Button>
          <Button variant="primary" type="submit" disabled={!isValid} loading={submitting}>
            {mode === 'create' ? 'Créer' : 'Enregistrer'}
          </Button>
        </div>
      </form>
    </div>
  );
}
